import { useState, useEffect, useMemo } from 'react';
import { sitesAPI, usersAPI, categoriesAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { ChevronsUpDown, Check } from 'lucide-react';
import {
  MapPin,
  Plus,
  Edit,
  Trash2,
  Search,
  Phone,
  Mail,
  User as UserIcon,
  Loader2,
  Home,
  Building2,
} from 'lucide-react';
import { ClientStatusPills, ClientAVBackupsFields } from '@/components/ClientStatusBadges';

const EMPTY_FORM = {
  customer_id: '',
  name: '',
  address: '',
  phone: '',
  email: '',
  primary_contact: '',
  notes: '',
  antivirus: '',
  backups: [],
};

export default function SitesModule() {
  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCustomerId, setFilterCustomerId] = useState('all');
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [antivirusOptions, setAntivirusOptions] = useState([]);
  const [backupTypeOptions, setBackupTypeOptions] = useState([]);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sitesRes, custRes, avRes, bkRes] = await Promise.all([
        sitesAPI.list(),
        usersAPI.list('customer'),
        categoriesAPI.list({ type: 'antivirus' }),
        categoriesAPI.list({ type: 'backup' }),
      ]);
      setSites(sitesRes.data || []);
      setCustomers(custRes.data || []);
      setAntivirusOptions((avRes.data || []).map((c) => c.name));
      setBackupTypeOptions((bkRes.data || []).map((c) => c.name));
    } catch (e) {
      console.error('Failed to load sites/customers', e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = sites.filter((s) => {
      if (filterCustomerId !== 'all' && s.customer_id !== filterCustomerId) return false;
      if (!q) return true;
      return [s.name, s.address, s.customer_name, s.primary_contact, s.email]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...list].sort((a, b) => {
      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      if (an < bn) return -1 * dir;
      if (an > bn) return 1 * dir;
      return 0;
    });
  }, [sites, search, filterCustomerId, sortDir]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, customer_id: filterCustomerId !== 'all' ? filterCustomerId : '' });
    setError('');
    setShowCreate(true);
  };

  const openEdit = (site) => {
    setEditingSite(site);
    setForm({
      customer_id: site.customer_id || '',
      name: site.name || '',
      address: site.address || '',
      phone: site.phone || '',
      email: site.email || '',
      primary_contact: site.primary_contact || '',
      notes: site.notes || '',
      antivirus: site.antivirus || '',
      backups: Array.isArray(site.backups)
        ? site.backups.map((b) => ({
            type: b.type || '',
            status: b.status || 'successful',
            last_updated_at: b.last_updated_at,
          }))
        : [],
    });
    setError('');
    setShowEdit(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.name.trim()) {
      setError('Customer and Site name are required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await sitesAPI.create({
        ...form,
        backups: (form.backups || []).filter((b) => (b.type || '').trim()),
      });
      setShowCreate(false);
      setForm({ ...EMPTY_FORM });
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create site');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Site name is required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await sitesAPI.update(editingSite.id, {
        ...form,
        backups: (form.backups || []).filter((b) => (b.type || '').trim()),
      });
      setShowEdit(false);
      setEditingSite(null);
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update site');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (site) => {
    if (!window.confirm(`Delete site "${site.name}"? Tickets attached to this site will be untagged but not deleted.`)) return;
    try {
      await sitesAPI.delete(site.id);
      loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete site');
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-slate-50 min-w-0" data-testid="sites-view">
      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto min-w-0">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Home className="w-6 h-6 text-cyan-600" />
              <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Sites</h1>
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 px-3 py-0.5 text-sm font-semibold tabular-nums"
                data-testid="sites-total-count"
                aria-label={`Total sites: ${sites.length}`}
              >
                {sites.length} {sites.length === 1 ? 'site' : 'sites'}
              </span>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Physical locations linked to a customer. Each customer can have many sites.
              When creating a ticket, the site is auto-picked if the customer has only one.
            </p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button
                onClick={openCreate}
                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full h-11"
                data-testid="sites-add-btn"
                disabled={customers.length === 0}
                title={customers.length === 0 ? 'Create a customer first' : 'Add a site'}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Site
              </Button>
            </DialogTrigger>
            <SiteFormDialogContent
              title="Add Site"
              description="Link a new physical location to a customer."
              form={form}
              setForm={setForm}
              customers={customers}
              antivirusOptions={antivirusOptions}
              backupTypeOptions={backupTypeOptions}
              error={error}
              submitting={submitting}
              onSubmit={handleCreate}
              onCancel={() => setShowCreate(false)}
              testIdPrefix="create-site"
              isEdit={false}
            />
          </Dialog>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search sites by name, address, customer, contact..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10 bg-white"
                data-testid="sites-search-input"
              />
            </div>
            <div className="sm:w-72">
              <Select value={filterCustomerId} onValueChange={setFilterCustomerId}>
                <SelectTrigger className="h-10 bg-white" data-testid="sites-filter-customer">
                  <SelectValue placeholder="All customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  {[...customers].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:w-56">
              <Select value={sortDir} onValueChange={setSortDir}>
                <SelectTrigger className="h-10 bg-white" data-testid="sites-sort-dir">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Name (A → Z)</SelectItem>
                  <SelectItem value="desc">Name (Z → A)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center text-slate-500 flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading sites...
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <MapPin className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="font-medium text-slate-700 mb-1">No sites yet</p>
                <p className="text-sm text-slate-500">
                  {customers.length === 0
                    ? 'Create a customer first, then add their sites here.'
                    : 'Click "Add Site" to link a physical location to a customer.'}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[280px]">Site</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((s) => (
                    <TableRow key={s.id} data-testid={`site-row-${s.id}`}>
                      <TableCell>
                        <div className="font-medium text-slate-800 flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-cyan-600 shrink-0" />
                          <span data-testid={`site-name-${s.id}`}>{s.name}</span>
                        </div>
                        <ClientStatusPills entity={s} entityId={s.id} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm text-slate-700">
                          <Building2 className="w-3.5 h-3.5 text-slate-400" />
                          {s.customer_name || '—'}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{s.address || '—'}</TableCell>
                      <TableCell className="text-sm text-slate-600">
                        <div className="space-y-0.5">
                          {s.primary_contact && (
                            <div className="flex items-center gap-1"><UserIcon className="w-3.5 h-3.5 text-slate-400" />{s.primary_contact}</div>
                          )}
                          {s.phone && (
                            <div className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-slate-400" />{s.phone}</div>
                          )}
                          {s.email && (
                            <div className="flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-slate-400" />{s.email}</div>
                          )}
                          {!s.primary_contact && !s.phone && !s.email && '—'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEdit(s)}
                            data-testid={`site-edit-${s.id}`}
                            aria-label="Edit site"
                          >
                            <Edit className="w-4 h-4 text-slate-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleDelete(s)}
                            data-testid={`site-delete-${s.id}`}
                            aria-label="Delete site"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit dialog */}
        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <SiteFormDialogContent
            title="Edit Site"
            description="Update this site's information."
            form={form}
            setForm={setForm}
            customers={customers}
            antivirusOptions={antivirusOptions}
            backupTypeOptions={backupTypeOptions}
            error={error}
            submitting={submitting}
            onSubmit={handleUpdate}
            onCancel={() => { setShowEdit(false); setEditingSite(null); }}
            testIdPrefix="edit-site"
            isEdit={true}
          />
        </Dialog>
      </div>
    </div>
  );
}

function CustomerCombobox({ customers, value, onChange, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const sorted = useMemo(
    () => [...customers].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [customers]
  );
  // Filter in JS so the render order is always the alphabetically-sorted
  // source order (cmdk's internal fuzzy score reorders matches otherwise).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((c) => (c.name || '').toLowerCase().includes(q));
  }, [sorted, query]);
  const selected = sorted.find((c) => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal bg-white border-slate-200"
          data-testid={`${testIdPrefix}-customer`}
        >
          <span className={selected ? 'text-slate-800 truncate' : 'text-slate-400'}>
            {selected ? selected.name : 'Search and pick a customer…'}
          </span>
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 z-[70]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search customer by name…"
            value={query}
            onValueChange={setQuery}
            data-testid={`${testIdPrefix}-customer-search`}
          />
          <CommandList>
            <CommandEmpty>No customers found.</CommandEmpty>
            <CommandGroup>
              {filtered.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.name}
                  onSelect={() => { onChange(c.id); setOpen(false); setQuery(''); }}
                  data-testid={`${testIdPrefix}-customer-option-${c.id}`}
                >
                  <Check className={`w-4 h-4 mr-2 ${value === c.id ? 'opacity-100 text-cyan-600' : 'opacity-0'}`} />
                  <span className="truncate">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SiteFormDialogContent({ title, description, form, setForm, customers, antivirusOptions, backupTypeOptions, error, submitting, onSubmit, onCancel, testIdPrefix, isEdit }) {
  return (
    <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4 mt-2">
        <div className="space-y-1.5">
          <Label>Customer *</Label>
          <CustomerCombobox
            customers={customers}
            value={form.customer_id || ''}
            onChange={(v) => setForm((p) => ({ ...p, customer_id: v }))}
            testIdPrefix={testIdPrefix}
          />
          {isEdit && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Heads up — changing the customer moves this site under a new owner. Existing tickets that already reference this site keep their original customer (only future tickets follow the new owner).
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Site name *</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="e.g. Headquarters, Warehouse, Clinic A"
            data-testid={`${testIdPrefix}-name`}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label>Address</Label>
          <Input
            value={form.address}
            onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
            placeholder="123 Main St, City, State ZIP"
            data-testid={`${testIdPrefix}-address`}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Primary contact</Label>
            <Input
              value={form.primary_contact}
              onChange={(e) => setForm((p) => ({ ...p, primary_contact: e.target.value }))}
              placeholder="Jane Smith"
              data-testid={`${testIdPrefix}-contact`}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              placeholder="+1 555 123 4567"
              data-testid={`${testIdPrefix}-phone`}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            placeholder="site@customer.com"
            data-testid={`${testIdPrefix}-email`}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Notes</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            placeholder="Door codes, parking, hours, etc."
            rows={3}
            data-testid={`${testIdPrefix}-notes`}
          />
        </div>

        <ClientAVBackupsFields
          data={form}
          onChange={(patch) => setForm((p) => ({ ...p, ...patch }))}
          antivirusOptions={antivirusOptions || []}
          backupTypeOptions={backupTypeOptions || []}
          testIdPrefix={testIdPrefix}
        />

        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onCancel} data-testid={`${testIdPrefix}-cancel`}>Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid={`${testIdPrefix}-submit`}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? 'Save changes' : 'Create site')}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}
